import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, ButtonGroup, Dropdown } from 'react-bootstrap';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { ListTodo, Plus, Filter, CheckCircle, Search, X, Users, ArrowUpDown, AlertCircle, Wrench, Check, SquareKanban, CalendarDays } from 'lucide-react';
import { IssueWithBuilding, IssuePriority, IssueComponentCategory } from '../../types/issues';
import { issuePriorityLabels } from '../../types/issuePriorityDisplay';
import { useWorkOrders } from '../../hooks/workOrders/useWorkOrders';
import { useCompletedWorkOrders } from '../../hooks/workOrders/useCompletedWorkOrders';
import { useIssues } from '../../hooks/issues/useIssues';
import { useBuildingChecks } from '../../hooks/management/useBuildingChecks';
import { useUpcomingMaintenance, UpcomingMaintenanceItem } from '../../hooks/management/useUpcomingMaintenance';
import { useProjectTitles } from '../../hooks/projects/useProjectTitles';
// Note: useCompletedInspections is available but not used - completed inspections
// are not displayed in the "Klart" column (no CompletedInspectionCard component yet)
import { useUsersData, Userprofile, toExternalUsers } from '../../Helpers/UsersContext';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { WorkOrderWithBuilding, WorkOrderStatus, workOrderPriorityLabels, getAssignees, getDelegations, isUnassigned } from '../../types/workOrders';
import { AssignableRecord } from '../../types/assignees';
import { BuildingType } from '../../types/BuildingTypeEnums';
import { useBuildingTagFilter } from '../../hooks/useBuildingTagFilter';
import { useBuildingKeyFilter } from '../../hooks/useBuildingKeyFilter';
import { UNTAGGED_OPTION_VALUE } from '../../hooks/mapOverview/buildingSort';
import FilterCheckboxColumn, { FilterToggleRow } from './components/FilterCheckboxColumn';
import { translate } from '../../types/EnumTranslations';
import ColumnCard from './components/ColumnCard';
import IssueCard from './components/IssueCard';
import MaintenanceCard from './components/MaintenanceCard';
import TaskCard, { UnifiedTask, workOrderToTask, inspectionToTask } from './components/TaskCard';
import { BuildingCheckWithTemplate } from '../BuildingChecks/buildingChecksUtils';
import { useCheckComponentNames } from '../../hooks/inspections/useCheckComponentNames';
import CreateWorkOrderModal from './components/CreateWorkOrderModal';
import IssueDetailModal from './components/IssueDetailModal';
import AssignWorkOrderModal from '../../Components/AssignWorkOrderModal';
import DueDateModal from './components/DueDateModal';
import TaskDetailModal from './components/TaskDetailModal';
import WorkOrderDetailModal from './components/WorkOrderDetailModal';
import ArchiveMenuDropdown from './components/ArchiveMenuDropdown';
import ArchivedWorkOrdersModal from './components/ArchivedWorkOrdersModal';
import ArchivedIssuesModal from './components/ArchivedIssuesModal';
import MaintenanceDetailModal from './components/MaintenanceDetailModal';
import SnoozedSection from './components/SnoozedSection';
import IncomingColumn from './components/IncomingColumn';
import BuildingGroupList from './components/BuildingGroupList';
import ManagementCalendar from './components/ManagementCalendar';
import { CalendarEntry } from './utils/calendarEntries';
import { CalendarMonth, monthOf } from './utils/calendarMonth';
import { groupByDateBuckets, DateBucket } from './utils/dateGrouping';
import { loadFromInstanceStorage, saveToInstanceStorage } from '../../Helpers/instanceStorage';
import { getDaysUntilDue } from '../../Helpers/DateUtils';
import { getAuth } from 'firebase/auth';
import { BuildingSummary } from '../../types/protocols';
import './ManagementPage.css';

// LocalStorage key for building type filter persistence
const STORAGE_KEY_BUILDING_TYPES = 'managementPage_selectedBuildingTypes';

// LocalStorage key for building group tag filter persistence (stores the
// DESELECTED tag options — see useBuildingTagFilter)
const STORAGE_KEY_BUILDING_TAGS = 'managementPage_deselectedBuildingTags';

// LocalStorage key for the "Visa delmoment från projekt" filter. Off by default:
// project delmoment live on the Projekt board, and Förvaltning is the board for
// loose, non-project work.
const STORAGE_KEY_SHOW_PROJECT_DELMOMENT = 'managementPage_showProjectDelmoment';

// Detail modals are addressable via the URL query string so a reload (or a shared
// link) reopens the same modal. Each maps to one search param; they are mutually
// exclusive (one detail view at a time), so opening one clears the others.
const DETAIL_MODAL_PARAMS = ['wo', 'issue', 'maintenance', 'inspection'] as const;

// All available building types
const ALL_BUILDING_TYPES: BuildingType[] = [
    BuildingType.standard,
    BuildingType.complement,
    BuildingType.toiletArchipelago,
    BuildingType.toiletReserve,
    BuildingType.other,
];

/**
 * Load selected building types from localStorage
 */
function loadBuildingTypesFromStorage(): BuildingType[] {
    const stored = loadFromInstanceStorage<BuildingType[]>(STORAGE_KEY_BUILDING_TYPES, ALL_BUILDING_TYPES);
    // Tolerate corrupt storage and validate that values are BuildingType enums
    if (!Array.isArray(stored)) return ALL_BUILDING_TYPES;
    return stored.filter(v => ALL_BUILDING_TYPES.includes(v));
}

/**
 * Save selected building types to localStorage
 */
function saveBuildingTypesToStorage(types: BuildingType[]): void {
    saveToInstanceStorage(STORAGE_KEY_BUILDING_TYPES, types);
}

// How far in advance upcoming maintenance and inspections are shown in the
// "Att hantera" column. Fixed for now (not user-configurable); overdue items
// always show regardless. The column's info tooltip communicates this.
const LOOKAHEAD_DAYS = 365;
const LOOKAHEAD_INFO = 'Visar underhåll och kontroller som förfaller inom 1 år, samt försenade.';

// Time buckets for the "Att hantera" subcolumns. Items fall into the first
// bucket whose threshold (in days) they don't exceed; the last bucket also
// catches anything older/further out so nothing is hidden. Labels use rolling
// windows (last/within N days) to match the rolling day thresholds.
// Felanmälningar are grouped by how recently they were reported (createdAt).
const ISSUE_BUCKETS: DateBucket[] = [
    { label: 'Senaste 7 dagarna', maxDays: 7 },
    { label: 'Senaste 30 dagarna', maxDays: 30 },
    { label: 'Senaste 90 dagarna', maxDays: 90 },
    { label: 'Senaste året', maxDays: 365 }
];
// Underhåll & kontroller are grouped by when they fall due. Overdue items
// (negative days) land in "Förfallit" first.
const DUE_BUCKETS: DateBucket[] = [
    { label: 'Förfallit', maxDays: -1 },
    { label: 'Förfaller inom 7 dagar', maxDays: 7 },
    { label: 'Förfaller inom 30 dagar', maxDays: 30 },
    { label: 'Förfaller inom 90 dagar', maxDays: 90 },
    { label: 'Förfaller inom ett år', maxDays: 365 }
];

// Board sort. "dueDate" is the board as it has always been (date sections,
// 20-card lists); "building" groups every list by building, alphabetically,
// each group collapsed until opened. Not persisted: every visit starts by date.
type BoardSort = 'dueDate' | 'building';
const BOARD_SORT_LABELS: Record<BoardSort, string> = {
    dueDate: 'Förfallodatum',
    building: 'Byggnad'
};

// Board (Tavla) or month calendar (Kalender). Not persisted: every visit starts on Tavla.
type BoardView = 'board' | 'calendar';

// Toggle icons of the building groups, one per list
const GROUP_ICON_SIZE = 14;

/**
 * ManagementPage - Kanban-style operational dashboard showing 4 columns:
 * 1. Att hantera - Issues, maintenance, and inspections awaiting triage
 * 2. Ärenden (ej delegerade) - Open work orders nobody is assigned or delegated
 * 3. Delegerade/planerade - Assigned or delegated work orders + active inspections
 * 4. Klart [year] - Completed work orders
 *
 * Work orders linked to a project (delmoment) are hidden unless the
 * "Visa delmoment från projekt" filter is on; the Projekt board is their home.
 */
const ManagementPage: React.FC = () => {
    // Set document title
    useDocumentTitle('Förvaltning');

    // Phones show one zone of the board at a time, picked with tabs; desktop
    // keeps the full sideways-scrolling board. See the phone rules in ManagementPage.css.
    const isSmallScreen = useIsSmallScreen();
    const [mobileZone, setMobileZone] = useState<'att-hantera' | 'planering' | 'klart'>('att-hantera');

    // Data hooks
    const { buildingChecks, snoozedBuildingChecks, loading: checksLoading, refresh: refreshChecks, snoozeBuildingCheck, unsnoozeBuildingCheck } = useBuildingChecks();
    // A component check is named after its component ("Brandsläckare – Brandsläckare (Plan 2)")
    const allBuildingChecks = useMemo(() => [...buildingChecks, ...snoozedBuildingChecks], [buildingChecks, snoozedBuildingChecks]);
    const componentNameOf = useCheckComponentNames(allBuildingChecks);
    const checkToTask = useCallback(
        (check: BuildingCheckWithTemplate) => inspectionToTask(check, key => componentNameOf(check.buildingId, key)),
        [componentNameOf]
    );
    const {
        workOrders,
        loading: workOrdersLoading,
        updateWorkOrderDueDate,
        reload: refreshWorkOrders
    } = useWorkOrders();
    const { issues, snoozedIssues, loading: issuesLoading, refresh: refreshIssues, archiveDirectly, snoozeIssue, unsnoozeIssue } = useIssues();
    const { maintenance, snoozedMaintenance, loading: maintenanceLoading, refresh: refreshMaintenance, snoozeMaintenance, unsnoozeMaintenance } = useUpcomingMaintenance();
    const { users, refreshUsers } = useUsersData();
    const { buildings } = useBuildingsData();
    const auth = getAuth();

    // Check if current user is admin
    const isAdmin = users?.find(u => u.key === auth.currentUser?.uid)?.admin || false;

    // Tavla or Kalender (session only), and the calendar's month
    const [boardView, setBoardView] = useState<BoardView>('board');
    const showCalendar = boardView === 'calendar';
    const [calendarMonth, setCalendarMonth] = useState<CalendarMonth>(() => monthOf(new Date()));

    // Completed work orders: the Klart column shows the current year; the calendar
    // the year of the month on screen (the column is hidden meanwhile).
    const currentYear = new Date().getFullYear();
    const {
        workOrders: completedWorkOrders,
        loading: completedLoading,
        refresh: refreshCompleted
    } = useCompletedWorkOrders(showCalendar ? calendarMonth.year : currentYear);

    // Whether work orders linked to projects (delmoment) are shown on this board
    // (persisted; off by default). Project titles are only fetched when on.
    const [showProjectDelmoment, setShowProjectDelmoment] = useState<boolean>(
        () => loadFromInstanceStorage<boolean>(STORAGE_KEY_SHOW_PROJECT_DELMOMENT, false) === true
    );
    useEffect(() => {
        saveToInstanceStorage(STORAGE_KEY_SHOW_PROJECT_DELMOMENT, showProjectDelmoment);
    }, [showProjectDelmoment]);
    const projectTitles = useProjectTitles(showProjectDelmoment);

    // Delmoment carry their project's title on the card; loose work orders none.
    const projectTitleFor = useCallback((wo: WorkOrderWithBuilding): string | undefined => {
        if (!showProjectDelmoment || !wo.projectKey) return undefined;
        return projectTitles.get(wo.projectKey) ?? 'Projekt';
    }, [showProjectDelmoment, projectTitles]);


    // Building type filter state (persisted in localStorage)
    const [selectedBuildingTypes, setSelectedBuildingTypes] = useState<BuildingType[]>(loadBuildingTypesFromStorage);

    // Persist building type filter changes
    useEffect(() => {
        saveBuildingTypesToStorage(selectedBuildingTypes);
    }, [selectedBuildingTypes]);

    // Free-text search filtering all card columns (not persisted — resets on reload)
    const [searchQuery, setSearchQuery] = useState('');
    const normalizedSearchQuery = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);

    // True if any of the given texts (card title, building name, etc.) contain
    // the search query. Empty query matches everything.
    const matchesSearchQuery = useCallback((...texts: (string | undefined)[]): boolean => {
        if (!normalizedSearchQuery) return true;
        return texts.some(t => t?.toLowerCase().includes(normalizedSearchQuery));
    }, [normalizedSearchQuery]);

    // ---- Sort by building (session only) ----
    const [boardSort, setBoardSort] = useState<BoardSort>('dueDate');
    const groupedByBuilding = boardSort === 'building';

    // Groups start collapsed. The user's own open groups are kept apart from
    // the ones closed during a search: while searching every group shows open
    // (so hits aren't hidden) unless closed in that search, and clearing the
    // search brings the user's own state back. Group ids are `${list}:${buildingKey}`.
    const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
    const [groupsClosedInSearch, setGroupsClosedInSearch] = useState<Set<string>>(() => new Set());
    useEffect(() => {
        setGroupsClosedInSearch(new Set());
    }, [normalizedSearchQuery]);
    const isGroupExpanded = useCallback((groupId: string): boolean =>
        normalizedSearchQuery ? !groupsClosedInSearch.has(groupId) : openGroups.has(groupId),
    [normalizedSearchQuery, groupsClosedInSearch, openGroups]);
    const toggleGroup = useCallback((groupId: string) => {
        const toggleIn = (prev: Set<string>) => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
            return next;
        };
        if (normalizedSearchQuery) setGroupsClosedInSearch(toggleIn);
        else setOpenGroups(toggleIn);
    }, [normalizedSearchQuery]);

    // Whether an item due on `dateStr` falls within the look-ahead window. Overdue
    // items (negative days) always pass so they keep showing as "late".
    const withinLookAhead = useCallback((dateStr?: string): boolean => {
        if (!dateStr) return true;
        return getDaysUntilDue(dateStr) <= LOOKAHEAD_DAYS;
    }, []);

    // Create a map of buildingKey -> BuildingType for fast lookups
    const buildingTypeMap = useMemo(() => {
        const map = new Map<string, BuildingType>();
        if (buildings) {
            buildings.forEach(building => {
                if (building.key && building.buildingType) {
                    map.set(building.key, building.buildingType);
                }
            });
        }
        return map;
    }, [buildings]);

    // Building summaries by key, for the building groups' cover, type and tags
    const buildingsByKey = useMemo(() => {
        const map = new Map<string, BuildingSummary>();
        buildings?.forEach(building => map.set(building.key, building));
        return map;
    }, [buildings]);

    // Helper to check if an item's building matches the filter
    const matchesBuildingTypeFilter = useCallback((buildingKey: string): boolean => {
        // If all types selected, show everything
        if (selectedBuildingTypes.length === ALL_BUILDING_TYPES.length) {
            return true;
        }
        // If no types selected, show nothing
        if (selectedBuildingTypes.length === 0) {
            return false;
        }
        const buildingType = buildingTypeMap.get(buildingKey);
        // If building type unknown, include it (don't hide due to missing data)
        if (!buildingType) {
            return true;
        }
        return selectedBuildingTypes.includes(buildingType);
    }, [selectedBuildingTypes, buildingTypeMap]);

    // Toggle a building type in the filter
    const toggleBuildingType = (type: BuildingType) => {
        setSelectedBuildingTypes(prev => {
            if (prev.includes(type)) {
                return prev.filter(t => t !== type);
            } else {
                return [...prev, type];
            }
        });
    };

    // Select/deselect all building types
    const toggleAllBuildingTypes = () => {
        if (selectedBuildingTypes.length === ALL_BUILDING_TYPES.length) {
            setSelectedBuildingTypes([]);
        } else {
            setSelectedBuildingTypes(ALL_BUILDING_TYPES);
        }
    };

    // Building group tag filter (shared hook; persists deselections so tags
    // created later default to visible)
    const {
        availableTags,
        tagOptionCount,
        selectedTagOptionCount,
        tagFilterActive,
        allTagOptionsSelected,
        isTagOptionSelected,
        toggleTagOption,
        toggleAllTagOptions,
        matchesBuildingTagFilter,
    } = useBuildingTagFilter(STORAGE_KEY_BUILDING_TAGS);

    // Specific-building filter (session only, see the hook)
    const {
        buildingOptions,
        allBuildingsSelected,
        buildingKeyFilterActive,
        isBuildingSelected,
        toggleBuilding,
        toggleAllBuildings,
        matchesBuildingKeyFilter,
    } = useBuildingKeyFilter(buildings);

    // Combined building filter: an item shows only if its building passes the
    // type filter, the group tag filter and the building filter
    const matchesBuildingFilter = useCallback((buildingKey: string): boolean => {
        return matchesBuildingTypeFilter(buildingKey)
            && matchesBuildingTagFilter(buildingKey)
            && matchesBuildingKeyFilter(buildingKey);
    }, [matchesBuildingTypeFilter, matchesBuildingTagFilter, matchesBuildingKeyFilter]);

    // Summary state for the shared filter dropdown button. When no tags are
    // defined the tag column is hidden and the counts cover only building types.
    // The building column only colours the button active; its rows are not
    // counted, since "47/49" says nothing on a large instance.
    const tagColumnVisible = availableTags.length > 0;
    const buildingColumnVisible = buildingOptions.length > 0;
    const typeFilterActive = selectedBuildingTypes.length < ALL_BUILDING_TYPES.length;
    const buildingFilterActive = typeFilterActive || tagFilterActive || buildingKeyFilterActive;
    const anyFilterActive = buildingFilterActive || showProjectDelmoment;
    const selectedFilterCount = selectedBuildingTypes.length
        + (tagColumnVisible ? selectedTagOptionCount : 0);
    const totalFilterCount = ALL_BUILDING_TYPES.length + (tagColumnVisible ? tagOptionCount : 0);

    // Detail modals are reflected in the URL query string (see DETAIL_MODAL_PARAMS)
    // so a reload reopens the same modal. The URL holds only the item's key; the
    // object shown is re-derived from the loaded lists below.
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Open a detail modal by writing its key to the URL. Detail modals are mutually
    // exclusive, so clear the others first.
    const openDetailModal = useCallback((param: string, value: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            DETAIL_MODAL_PARAMS.forEach(p => next.delete(p));
            next.set(param, value);
            return next;
        });
    }, [setSearchParams]);

    // Close a detail modal by removing its param from the URL.
    const closeDetailModal = useCallback((param: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete(param);
            return next;
        });
    }, [setSearchParams]);

    // Work order detail — the modal fetches by key internally, so the raw key is
    // all we need (it opens even before the board's lists have loaded on reload).
    const viewingWorkOrderKey = searchParams.get('wo');

    // Issue detail — re-derive the full issue from the loaded lists by key.
    const issueParam = searchParams.get('issue');
    const selectedIssue = useMemo<IssueWithBuilding | null>(() => {
        if (!issueParam) return null;
        return [...issues, ...snoozedIssues].find(i => i.key === issueParam) ?? null;
    }, [issueParam, issues, snoozedIssues]);

    // Maintenance detail — key is `buildingKey_componentKey_templateId`.
    const maintenanceParam = searchParams.get('maintenance');
    const viewingMaintenance = useMemo<UpcomingMaintenanceItem | null>(() => {
        if (!maintenanceParam) return null;
        return [...maintenance, ...snoozedMaintenance].find(m => m.key === maintenanceParam) ?? null;
    }, [maintenanceParam, maintenance, snoozedMaintenance]);

    // Inspection ("kontroll") detail — key is `buildingId-checkKey`; rebuild the
    // UnifiedTask from the matching building check.
    const inspectionParam = searchParams.get('inspection');
    const viewingTask = useMemo<UnifiedTask | null>(() => {
        if (!inspectionParam) return null;
        const check = [...buildingChecks, ...snoozedBuildingChecks]
            .find(c => `${c.buildingId}-${c.checkKey}` === inspectionParam);
        return check ? checkToTask(check) : null;
    }, [inspectionParam, buildingChecks, snoozedBuildingChecks, checkToTask]);

    // Modal state for issue conversion to work order
    const [convertingIssue, setConvertingIssue] = useState<IssueWithBuilding | null>(null);
    // The component's name or the category, for the work order's prefilled title.
    const [convertingIssueLabel, setConvertingIssueLabel] = useState<string | undefined>(undefined);

    // Modal state for maintenance conversion to work order
    const [convertingMaintenance, setConvertingMaintenance] = useState<UpcomingMaintenanceItem | null>(null);

    // Modal state for standalone work order creation
    const [showCreateWorkOrder, setShowCreateWorkOrder] = useState(false);

    // Source work order being duplicated (opens the create modal pre-filled)
    const [duplicateSource, setDuplicateSource] = useState<WorkOrderWithBuilding | null>(null);

    // Modal state for delegation
    // Key of the work order open in the Tilldela modal; the live object is
    // looked up from the list so the modal sees refreshed assignees.
    const [assigningWorkOrderKey, setAssigningWorkOrderKey] = useState<string | null>(null);
    const assigningWorkOrder = useMemo(
        () => (assigningWorkOrderKey ? workOrders.find((wo) => wo.key === assigningWorkOrderKey) ?? null : null),
        [assigningWorkOrderKey, workOrders]
    );

    // Modal state for quick due date editing; dueDateProposal is the day a work
    // order was dropped on in the calendar, confirmed in the same modal
    const [dueDateTask, setDueDateTask] = useState<UnifiedTask | null>(null);
    const [dueDateProposal, setDueDateProposal] = useState<string | undefined>(undefined);

    // Archive modal state
    const [showArchivedWorkOrders, setShowArchivedWorkOrders] = useState(false);
    const [showArchivedIssues, setShowArchivedIssues] = useState(false);

    // Create a Map for O(1) user name lookups
    const userNameMap = useMemo(() => {
        const map = new Map<string, string>();
        users?.forEach((u: Userprofile) => {
            map.set(u.key, u.name || u.key);
        });
        return map;
    }, [users]);

    // Helper to get user name from UID (O(1) lookup)
    const getUserName = useCallback((uid: string): string => {
        return userNameMap.get(uid) || uid;
    }, [userNameMap]);

    // Names of everyone associated with a work order or project — assignees,
    // external delegates, and (for work orders) whoever completed it — so
    // searching "Jasper" finds cards assigned to or completed by Jasper.
    const getAssociatedNames = useCallback((resource: AssignableRecord, completedBy?: string): (string | undefined)[] => {
        return [
            ...Object.keys(getAssignees(resource)).map(getUserName),
            ...Object.keys(getDelegations(resource)).map(getUserName),
            completedBy ? getUserName(completedBy) : undefined
        ];
    }, [getUserName]);

    // Filter active internal users for assignment dropdown
    const activeUsers = useMemo(() => {
        if (!users) return [];
        return users.filter((u: Userprofile) => u.accessToWeb && !u.deleted && !u.isExternal);
    }, [users]);

    const externalUserList = useMemo(() => toExternalUsers(users), [users]);

    // Open work orders on the board. Work orders linked to projects (delmoment)
    // are managed on the Projekt board and only show here when the "Visa
    // delmoment från projekt" filter is on.
    const openWorkOrders = useMemo(() => workOrders
        .filter((wo: WorkOrderWithBuilding) => wo.status === WorkOrderStatus.OPEN)
        .filter(wo => matchesBuildingFilter(wo.buildingKey))
        .filter(wo => showProjectDelmoment || !wo.projectKey)
        .filter(wo => matchesSearchQuery(wo.title, wo.buildingName, 'Ärende', wo.unitLabel, wo.priority && workOrderPriorityLabels[wo.priority], projectTitleFor(wo), ...getAssociatedNames(wo, wo.completedBy))),
    [workOrders, matchesBuildingFilter, showProjectDelmoment, projectTitleFor, matchesSearchQuery, getAssociatedNames]);

    // Sort helper for tasks by due date
    const sortByDueDate = useCallback((a: UnifiedTask, b: UnifiedTask) => {
        // Overdue items first
        if (a.isOverdue && !b.isOverdue) return -1;
        if (!a.isOverdue && b.isOverdue) return 1;

        // Then by due date (nulls last)
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return a.dueDate.localeCompare(b.dueDate);
    }, []);

    // Work orders as tasks: due date order, and among those without a due date
    // the newest first (creation time is the meaningful order there).
    const workOrdersToTasks = useCallback((orders: WorkOrderWithBuilding[]): UnifiedTask[] =>
        [...orders]
            .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
            .map(wo => workOrderToTask(wo, projectTitleFor(wo)))
            .sort(sortByDueDate),
    [projectTitleFor, sortByDueDate]);

    // All active inspections (building checks always have nextDueDate)
    const checkTasks = useMemo(() => buildingChecks
        .filter(check => matchesBuildingFilter(check.buildingId || ''))
        .map(checkToTask)
        .filter(task => matchesSearchQuery(task.title, task.buildingName, 'Kontroll')),
    [buildingChecks, matchesBuildingFilter, checkToTask, matchesSearchQuery]);

    // Board columns, split by assignment (molnfast-react#1678): a work order
    // nobody is assigned or delegated is under Ärenden (ej delegerade), whatever
    // its due date; the rest, plus the inspections, under Delegerade/planerade.
    const unassignedTasks = useMemo(
        () => workOrdersToTasks(openWorkOrders.filter(wo => isUnassigned(wo))),
        [openWorkOrders, workOrdersToTasks]
    );
    const assignedTasks = useMemo(
        () => [...workOrdersToTasks(openWorkOrders.filter(wo => !isUnassigned(wo))), ...checkTasks].sort(sortByDueDate),
        [openWorkOrders, workOrdersToTasks, checkTasks, sortByDueDate]
    );

    // The calendar splits by due date instead: a work order without one sits in
    // its Ej planerade pane, to be dragged onto a day, whoever it is assigned to.
    const calendarUnplannedTasks = useMemo(
        () => workOrdersToTasks(openWorkOrders.filter(wo => !wo.dueDate)),
        [openWorkOrders, workOrdersToTasks]
    );
    const calendarPlannedTasks = useMemo(
        () => [...workOrdersToTasks(openWorkOrders.filter(wo => !!wo.dueDate)), ...checkTasks].sort(sortByDueDate),
        [openWorkOrders, workOrdersToTasks, checkTasks, sortByDueDate]
    );

    // Issue click handler
    const handleIssueClick = (issue: IssueWithBuilding) => {
        openDetailModal('issue', issue.key);
    };

    // Issue conversion handlers
    const handleConvertFromDetail = (latest: { componentKey?: string; componentCategory?: IssueComponentCategory; priority: IssuePriority; componentLabel?: string }) => {
        if (selectedIssue) {
            // Use the component link and priority reported live by the detail
            // modal, so just-made edits are carried into the work order even
            // before the issue list has refetched. selectedIssue is otherwise a
            // stale snapshot from when the card was clicked.
            setConvertingIssue({
                ...selectedIssue,
                componentKey: latest.componentKey,
                componentCategory: latest.componentCategory,
                priority: latest.priority
            });
            setConvertingIssueLabel(latest.componentLabel);
            closeDetailModal('issue');
        }
    };

    const handleIssueConverted = () => {
        setConvertingIssue(null);
        refreshIssues();
        refreshWorkOrders();
    };

    // Issue archive handler (archives without creating work order)
    const handleArchiveIssue = async (reason?: string) => {
        if (!selectedIssue) return;
        await archiveDirectly(selectedIssue.key, reason);
        closeDetailModal('issue');
    };

    // Maintenance click handler (opens detail modal first)
    const handleMaintenanceClick = (item: UpcomingMaintenanceItem) => {
        openDetailModal('maintenance', item.key);
    };

    // Handler for converting maintenance to work order (from detail modal)
    const handleMaintenanceConvert = () => {
        if (viewingMaintenance) {
            setConvertingMaintenance(viewingMaintenance);
        }
    };

    // Maintenance conversion handler
    const handleMaintenanceConverted = () => {
        setConvertingMaintenance(null);
        closeDetailModal('maintenance');
        refreshWorkOrders();
    };


    // Standalone work order creation handlers
    const handleCreateWorkOrder = () => {
        setShowCreateWorkOrder(true);
    };

    const handleWorkOrderCreated = () => {
        setShowCreateWorkOrder(false);
        refreshWorkOrders();
    };

    // Duplicate an existing work order: close the detail view and open the create
    // modal pre-filled from the source.
    const handleDuplicateWorkOrder = (workOrder: WorkOrderWithBuilding) => {
        closeDetailModal('wo');
        setDuplicateSource(workOrder);
    };

    const handleDuplicateCreated = () => {
        setDuplicateSource(null);
        refreshWorkOrders();
    };

    // Task click handler for To Do column (open modal)
    const handleTodoTaskClick = (task: UnifiedTask) => {
        if (task.type === 'workOrder') {
            // Use WorkOrderDetailModal for work orders (same as delegated column)
            openDetailModal('wo', task.key);
        } else {
            // Use TaskDetailModal for inspections only
            openDetailModal('inspection', task.key);
        }
    };

    // Delegated column: work orders open in the detail modal; inspections link to
    // the building checks page (taskPagePath, passed to the card as href).
    const taskPagePath = (task: UnifiedTask): string | undefined =>
        task.type === 'workOrder' ? undefined : `/building/${task.buildingKey}/checks`;

    // Task click handler for Completed column
    const handleCompletedClick = (workOrder: WorkOrderWithBuilding) => {
        openDetailModal('wo', workOrder.key);
    };

    // Show all completed work orders (opens archive modal pre-filtered)
    const handleShowAllCompleted = () => {
        setShowArchivedWorkOrders(true);
    };

    // Navigate to task detail (work orders open in the detail modal, inspections navigate to building checks)
    const navigateToTask = (task: UnifiedTask) => {
        if (task.type === 'workOrder') {
            openDetailModal('wo', task.key);
        } else {
            navigate(`/building/${task.buildingKey}/checks`);
        }
    };

    // Task detail modal handlers
    const handleTaskDetailClose = () => {
        closeDetailModal('inspection');
    };

    const handleTaskDetailAssign = (task: UnifiedTask) => {
        closeDetailModal('inspection');
        handleAssignTask(task);
    };

    const handleTaskDetailNavigate = (task: UnifiedTask) => {
        closeDetailModal('inspection');
        navigateToTask(task);
    };

    // Delegation handlers
    const handleAssignTask = (task: UnifiedTask) => {
        if (task.type === 'workOrder' && task.workOrder) {
            setAssigningWorkOrderKey(task.workOrder.key);
        }
        // TODO: Handle inspection assignment when inspection delegation is implemented
    };

    // Due date handlers
    const handleSetDueDateTask = (task: UnifiedTask) => {
        setDueDateProposal(undefined);
        setDueDateTask(task);
    };

    // Calendar drop: confirm the new day in the due date modal
    const handleCalendarReschedule = (task: UnifiedTask, day: string) => {
        setDueDateProposal(day);
        setDueDateTask(task);
    };

    // Calendar click: the same detail modals as the board
    const handleCalendarOpen = (entry: CalendarEntry) => {
        openDetailModal(entry.kind === 'inspection' ? 'inspection' : 'wo', entry.task.key);
    };

    const handleDueDateConfirm = async (dueDate: string | null) => {
        if (!dueDateTask?.workOrder) return;
        await updateWorkOrderDueDate(dueDateTask.workOrder.key, dueDate);
        setDueDateTask(null);
        setDueDateProposal(undefined);
    };

    const handleDueDateClose = () => {
        setDueDateTask(null);
        setDueDateProposal(undefined);
    };

    // Combined loading state for incoming column
    const incomingLoading = issuesLoading || maintenanceLoading;

    // Filter and sort issues by priority (most urgent first: HIGH → MEDIUM → LOW)
    const sortedIssues = useMemo(() => {
        const priorityOrder: Record<IssuePriority, number> = {
            [IssuePriority.HIGH]: 0,
            [IssuePriority.MEDIUM]: 1,
            [IssuePriority.LOW]: 2
        };
        return issues
            .filter(issue => matchesBuildingFilter(issue.buildingKey))
            .filter(issue => matchesSearchQuery(issue.description, issue.buildingName, 'Felanmälan', issuePriorityLabels[issue.priority]))
            .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
    }, [issues, matchesBuildingFilter, matchesSearchQuery]);

    // Activities that already have an OPEN work order are tracked there, not in
    // "Att hantera" — keyed by "{buildingKey}|{maintenanceTemplateId}". Using the
    // open set (workOrders are active orders) is what makes the lifecycle correct
    // for free: cancel a WO → it leaves this set → the activity reappears (reset);
    // complete it → it leaves the set and the trigger advances nextDate.
    const activitiesWithOpenWorkOrder = useMemo(() => {
        const set = new Set<string>();
        workOrders.forEach(wo => {
            if (wo.maintenanceTemplateId) {
                set.add(`${wo.buildingKey}|${wo.maintenanceTemplateId}`);
            }
        });
        return set;
    }, [workOrders]);

    // Filter maintenance by the building filters and the look-ahead window, and hide
    // activities already handled by an open work order.
    const filteredMaintenance = useMemo(() => {
        return maintenance.filter(item =>
            matchesBuildingFilter(item.buildingKey) &&
            withinLookAhead(item.nextDate) &&
            !activitiesWithOpenWorkOrder.has(`${item.buildingKey}|${item.templateId}`) &&
            matchesSearchQuery(item.activityName, item.buildingName, item.componentLabel, 'Underhåll')
        );
    }, [maintenance, matchesBuildingFilter, withinLookAhead, activitiesWithOpenWorkOrder, matchesSearchQuery]);

    // Filter completed work orders by the building filters (project delmoment
    // follow the same opt-in as the open columns)
    const filteredCompleted = useMemo(() => {
        return completedWorkOrders
            .filter(wo => matchesBuildingFilter(wo.buildingKey))
            .filter(wo => showProjectDelmoment || !wo.projectKey)
            .filter(wo => matchesSearchQuery(wo.title, wo.buildingName, 'Ärende', wo.unitLabel, wo.priority && workOrderPriorityLabels[wo.priority], projectTitleFor(wo), ...getAssociatedNames(wo, wo.completedBy)));
    }, [completedWorkOrders, matchesBuildingFilter, showProjectDelmoment, projectTitleFor, matchesSearchQuery, getAssociatedNames]);

    // Filter building checks by the building filters and the look-ahead window for the
    // "Att hantera" column inspection filter
    const filteredBuildingChecks = useMemo(() => {
        return buildingChecks.filter(check =>
            matchesBuildingFilter(check.buildingId || '') &&
            withinLookAhead(check.nextDueDate) &&
            matchesSearchQuery(check.template?.name, check.buildingName, 'Kontroll')
        );
    }, [buildingChecks, matchesBuildingFilter, withinLookAhead, matchesSearchQuery]);

    // Pre-compute completed work order tasks
    const completedWorkOrderTasks = useMemo(() => {
        return filteredCompleted.map(wo => workOrderToTask(wo, projectTitleFor(wo)));
    }, [filteredCompleted, projectTitleFor]);

    // Group "Att hantera" items into time buckets for the subcolumn subheaders.
    // Felanmälningar by age (days since reported); underhåll + kontroller
    // (merged and sorted soonest-first) by days until due.
    const issueGroups = groupByDateBuckets(
        sortedIssues,
        issue => -getDaysUntilDue(issue.createdAt),
        ISSUE_BUCKETS
    );
    const dueEntries = [
        ...filteredMaintenance.map(item => ({ kind: 'maintenance' as const, date: item.nextDate, buildingKey: item.buildingKey, buildingName: item.buildingName, maintenance: item })),
        ...filteredBuildingChecks.map(check => {
            const task = checkToTask(check);
            return { kind: 'check' as const, date: check.nextDueDate, buildingKey: task.buildingKey, buildingName: task.buildingName, task };
        })
    ].sort((a, b) => getDaysUntilDue(a.date || '') - getDaysUntilDue(b.date || ''));
    const dueGroups = groupByDateBuckets(
        dueEntries,
        entry => getDaysUntilDue(entry.date || ''),
        DUE_BUCKETS
    );

    // One card per list item, shared by both sorts
    const renderIssue = (issue: IssueWithBuilding) => (
        <IssueCard
            key={issue.key}
            issue={issue}
            onClick={() => handleIssueClick(issue)}
            isSelected={selectedIssue?.key === issue.key}
        />
    );
    const renderDueEntry = (entry: typeof dueEntries[number]) => entry.kind === 'maintenance' ? (
        <MaintenanceCard
            key={`m-${entry.maintenance.key}`}
            item={entry.maintenance}
            onClick={() => handleMaintenanceClick(entry.maintenance)}
        />
    ) : (
        <TaskCard
            key={entry.task.key}
            task={entry.task}
            onClick={() => handleTodoTaskClick(entry.task)}
            onAssign={handleAssignTask}
            onSetDueDate={handleSetDueDateTask}
            getUserName={getUserName}
        />
    );
    const renderUnassignedTask = (task: UnifiedTask) => (
        <TaskCard
            key={task.key}
            task={task}
            onClick={() => handleTodoTaskClick(task)}
            onAssign={handleAssignTask}
            onSetDueDate={handleSetDueDateTask}
            getUserName={getUserName}
        />
    );
    const renderAssignedTask = (task: UnifiedTask) => (
        <TaskCard
            key={task.key}
            task={task}
            onClick={() => openDetailModal('wo', task.key)}
            href={taskPagePath(task)}
            onAssign={handleAssignTask}
            onSetDueDate={handleSetDueDateTask}
            getUserName={getUserName}
        />
    );
    const renderCompletedTask = (task: UnifiedTask) => (
        <TaskCard
            key={task.key}
            task={task}
            onClick={() => task.workOrder && handleCompletedClick(task.workOrder)}
            getUserName={getUserName}
            isCompleted={true}
        />
    );

    // A list sorted by building: every card (no 20-card limit), grouped
    const groupListProps = { buildingsByKey, isExpanded: isGroupExpanded, onToggle: toggleGroup };
    const renderTaskGroups = (listId: string, tasks: UnifiedTask[], renderTask: (task: UnifiedTask) => React.ReactNode, itemLabel: string, itemIcon: React.ReactNode) => (
        <BuildingGroupList
            {...groupListProps}
            listId={listId}
            items={tasks}
            getBuildingKey={task => task.buildingKey}
            getBuildingName={task => task.buildingName}
            renderItem={renderTask}
            itemLabel={itemLabel}
            itemIcon={itemIcon}
        />
    );

    // Tab counts on phones: the same lists each zone renders below.
    const mobileZones = [
        { key: 'att-hantera' as const, label: 'Att hantera', count: sortedIssues.length + filteredMaintenance.length + filteredBuildingChecks.length },
        { key: 'planering' as const, label: 'Planering', count: unassignedTasks.length + assignedTasks.length },
        { key: 'klart' as const, label: 'Klart', count: filteredCompleted.length },
    ];

    return (
        <div className="management-page">
            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent items={[{ text: 'Förvaltning' }]} />
            </div>
            <div className="management-page__header">
                <h1 className="management-page__title page-title">Förvaltning</h1>
                <div className="management-page__search">
                    {/* Tavla | Kalender */}
                    <ButtonGroup aria-label="Visning" className="management-view-toggle">
                        <Button
                            variant={showCalendar ? 'outline-secondary' : 'primary'}
                            aria-pressed={!showCalendar}
                            aria-label="Tavla"
                            className="d-flex align-items-center gap-2"
                            onClick={() => setBoardView('board')}
                        >
                            <SquareKanban size={18} aria-hidden="true" />
                            <span className="management-filter-label">Tavla</span>
                        </Button>
                        <Button
                            variant={showCalendar ? 'primary' : 'outline-secondary'}
                            aria-pressed={showCalendar}
                            aria-label="Kalender"
                            className="d-flex align-items-center gap-2"
                            onClick={() => setBoardView('calendar')}
                        >
                            <CalendarDays size={18} aria-hidden="true" />
                            <span className="management-filter-label">Kalender</span>
                        </Button>
                    </ButtonGroup>
                    <div className="management-page__search-input">
                        <Search size={14} className="management-page__search-icon" />
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Sök..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                className="management-page__search-clear"
                                onClick={() => setSearchQuery('')}
                                aria-label="Rensa sökning"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>
                <div className="d-flex gap-2 management-page__actions">
                    {/* Building filter: type and group tag as columns in one dropdown */}
                    <Dropdown autoClose="outside">
                        <Dropdown.Toggle
                            variant={anyFilterActive ? 'primary' : 'outline-secondary'}
                            className="d-flex align-items-center gap-2"
                            aria-label="Filter"
                        >
                            <Filter size={18} aria-hidden="true" />
                            {/* Hidden on phones to keep the header row short. */}
                            <span className="management-filter-label">Filter</span>
                            <span className={`badge building-type-filter-badge ms-1${buildingFilterActive ? ' building-type-filter-badge--active' : ''}`}>
                                {selectedFilterCount}/{totalFilterCount}
                            </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="building-type-filter-menu building-filter-menu">
                            <div className="building-filter-columns">
                                <FilterCheckboxColumn
                                    title="Byggnadstyp"
                                    options={ALL_BUILDING_TYPES.map(type => ({ key: type, label: translate(type) }))}
                                    isSelected={(key) => selectedBuildingTypes.includes(key as BuildingType)}
                                    allSelected={selectedBuildingTypes.length === ALL_BUILDING_TYPES.length}
                                    onToggle={(key) => toggleBuildingType(key as BuildingType)}
                                    onToggleAll={toggleAllBuildingTypes}
                                />
                                {tagColumnVisible && (
                                    <FilterCheckboxColumn
                                        title="Byggnadsgrupp"
                                        options={[
                                            { key: UNTAGGED_OPTION_VALUE, label: 'Utan grupp' },
                                            ...availableTags.map(tag => ({ key: tag.key, label: tag.tag }))
                                        ]}
                                        isSelected={isTagOptionSelected}
                                        allSelected={allTagOptionsSelected}
                                        onToggle={toggleTagOption}
                                        onToggleAll={toggleAllTagOptions}
                                    />
                                )}
                                {buildingColumnVisible && (
                                    <FilterCheckboxColumn
                                        title="Byggnad"
                                        options={buildingOptions}
                                        isSelected={isBuildingSelected}
                                        allSelected={allBuildingsSelected}
                                        onToggle={toggleBuilding}
                                        onToggleAll={toggleAllBuildings}
                                        searchPlaceholder="Sök byggnad…"
                                    />
                                )}
                                <div className="building-filter-column">
                                    <Dropdown.Header>Projekt</Dropdown.Header>
                                    <FilterToggleRow
                                        label="Visa delmoment från projekt"
                                        checked={showProjectDelmoment}
                                        onToggle={() => setShowProjectDelmoment(prev => !prev)}
                                    />
                                    <div className="building-filter-column__hint">
                                        Ärenden som hör till ett projekt hanteras på Projekt-sidan och visas här bara när detta är påslaget.
                                    </div>
                                </div>
                            </div>
                        </Dropdown.Menu>
                    </Dropdown>
                    {/* Sort: by due date (the board's sections) or grouped by building; not used by the calendar */}
                    {!showCalendar && (
                    <Dropdown>
                        <Dropdown.Toggle
                            variant={groupedByBuilding ? 'primary' : 'outline-secondary'}
                            className="d-flex align-items-center gap-2"
                            aria-label={`Sortera: ${BOARD_SORT_LABELS[boardSort]}`}
                        >
                            <ArrowUpDown size={18} aria-hidden="true" />
                            {/* Hidden on phones like the Filter label */}
                            <span className="management-filter-label">{BOARD_SORT_LABELS[boardSort]}</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="management-sort-menu">
                            <Dropdown.Header>Sortera</Dropdown.Header>
                            {(Object.keys(BOARD_SORT_LABELS) as BoardSort[]).map(sort => (
                                <Dropdown.Item
                                    key={sort}
                                    as="button"
                                    active={boardSort === sort}
                                    aria-current={boardSort === sort ? 'true' : undefined}
                                    onClick={() => setBoardSort(sort)}
                                >
                                    <Check
                                        size={16}
                                        aria-hidden="true"
                                        className="management-sort-menu__check"
                                        style={{ visibility: boardSort === sort ? 'visible' : 'hidden' }}
                                    />
                                    {BOARD_SORT_LABELS[sort]}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    )}
                    {/* Phones get the round plus button instead (below), as in the iOS app */}
                    {!isSmallScreen && (
                    <Button
                        variant="primary"
                        onClick={handleCreateWorkOrder}
                        className="d-flex align-items-center gap-2"
                    >
                        <Plus size={18} />
                        Skapa ärende
                    </Button>
                    )}
                    <ArchiveMenuDropdown
                        onShowArchivedWorkOrders={() => setShowArchivedWorkOrders(true)}
                        onShowArchivedIssues={() => setShowArchivedIssues(true)}
                    />
                </div>
            </div>
            {showCalendar && (
                <ManagementCalendar
                    planned={calendarPlannedTasks}
                    completed={completedWorkOrderTasks}
                    unplanned={calendarUnplannedTasks}
                    loading={workOrdersLoading || checksLoading || completedLoading}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    onOpen={handleCalendarOpen}
                    onReschedule={handleCalendarReschedule}
                    getUserName={getUserName}
                    agenda={isSmallScreen}
                />
            )}
            {isSmallScreen && (
                <button
                    type="button"
                    className="management-create-fab"
                    onClick={handleCreateWorkOrder}
                    aria-label="Skapa ärende"
                >
                    <Plus size={30} strokeWidth={1.75} aria-hidden="true" />
                </button>
            )}
            {!showCalendar && isSmallScreen && (
                <div className="management-zone-tabs" role="tablist" aria-label="Visa del av tavlan">
                    {mobileZones.map(zone => (
                        <button
                            key={zone.key}
                            type="button"
                            role="tab"
                            aria-selected={mobileZone === zone.key}
                            className={`management-zone-tab${mobileZone === zone.key ? ' active' : ''}`}
                            onClick={() => setMobileZone(zone.key)}
                        >
                            <span>{zone.label}</span>
                            <span className="management-zone-tab__count">{zone.count}</span>
                        </button>
                    ))}
                </div>
            )}
            {!showCalendar && (
            <div className="management-page__container" data-mobile-zone={mobileZone}>
                <div className="management-columns">
                    {/* Phase 1: Att hantera - split into Felanmälningar and Underhåll & kontroller */}
                    <div className="management-phase-group management-phase-group--att-hantera">
                    <div className="management-column-wrapper">
                        <IncomingColumn
                            loading={incomingLoading}
                            titleInfo={LOOKAHEAD_INFO}
                            issueCount={sortedIssues.length}
                            issueItems={groupedByBuilding ? (
                                <BuildingGroupList
                                    {...groupListProps}
                                    listId="issues"
                                    items={sortedIssues}
                                    getBuildingKey={issue => issue.buildingKey}
                                    getBuildingName={issue => issue.buildingName}
                                    renderItem={renderIssue}
                                    itemLabel="Felanmälningar"
                                    itemIcon={<AlertCircle size={GROUP_ICON_SIZE} aria-hidden="true" />}
                                />
                            ) : issueGroups.map(group => (
                                <React.Fragment key={group.label}>
                                    <div className="column-group-header">{group.label}</div>
                                    {group.items.map(renderIssue)}
                                </React.Fragment>
                            ))}
                            maintenanceCount={filteredMaintenance.length + filteredBuildingChecks.length}
                            maintenanceItems={groupedByBuilding ? (
                                <BuildingGroupList
                                    {...groupListProps}
                                    listId="maintenance"
                                    items={dueEntries}
                                    getBuildingKey={entry => entry.buildingKey}
                                    getBuildingName={entry => entry.buildingName}
                                    renderItem={renderDueEntry}
                                    itemLabel="Underhåll"
                                    itemIcon={<Wrench size={GROUP_ICON_SIZE} aria-hidden="true" />}
                                />
                            ) : dueGroups.map(group => (
                                <React.Fragment key={group.label}>
                                    <div className="column-group-header">{group.label}</div>
                                    {group.items.map(renderDueEntry)}
                                </React.Fragment>
                            ))}
                            issueFooter={isAdmin && (
                                <SnoozedSection
                                    issues={snoozedIssues}
                                    maintenance={[]}
                                    buildingChecks={[]}
                                    onIssueClick={handleIssueClick}
                                    onMaintenanceClick={handleMaintenanceClick}
                                    onBuildingCheckClick={(check) => handleTodoTaskClick(checkToTask(check))}
                                    checkToTask={checkToTask}
                                />
                            )}
                            maintenanceFooter={isAdmin && (
                                <SnoozedSection
                                    issues={[]}
                                    maintenance={snoozedMaintenance}
                                    buildingChecks={snoozedBuildingChecks}
                                    onIssueClick={handleIssueClick}
                                    onMaintenanceClick={handleMaintenanceClick}
                                    onBuildingCheckClick={(check) => handleTodoTaskClick(checkToTask(check))}
                                    checkToTask={checkToTask}
                                />
                            )}
                        />
                    </div>
                    </div>

                    {/* Phase 2: Ärenden (ej delegerade) + Delegerade/planerade */}
                    <div className="management-phase-group management-phase-group--planering">
                    <div className="management-column-wrapper">
                        <ColumnCard
                            title="Ärenden (ej delegerade)"
                            icon={<ListTodo size={20} />}
                            count={unassignedTasks.length}
                            loading={workOrdersLoading}
                            emptyMessage="Inga ärenden att delegera"
                            subtitle=""
                        >
                            {groupedByBuilding
                                ? renderTaskGroups('unassigned', unassignedTasks, renderUnassignedTask, 'Ärenden', <ListTodo size={GROUP_ICON_SIZE} aria-hidden="true" />)
                                : unassignedTasks.slice(0, 20).map(renderUnassignedTask)}
                        </ColumnCard>
                    </div>

                    {/* Delegerade/planerade - assigned or delegated work orders + active inspections */}
                    <div className="management-column-wrapper">
                        <ColumnCard
                            title="Delegerade/planerade"
                            icon={<Users size={20} />}
                            count={assignedTasks.length}
                            loading={workOrdersLoading || checksLoading}
                            emptyMessage="Inga delegerade arbeten"
                            subtitle=""
                        >
                            {groupedByBuilding
                                ? renderTaskGroups('assigned', assignedTasks, renderAssignedTask, 'Ärenden', <ListTodo size={GROUP_ICON_SIZE} aria-hidden="true" />)
                                : assignedTasks.slice(0, 20).map(renderAssignedTask)}
                        </ColumnCard>
                    </div>
                    </div>

                    {/* Phase 3: Klart [year] - Completed work orders */}
                    <div className="management-phase-group management-phase-group--klart">
                    <div className="management-column-wrapper">
                        <ColumnCard
                            title={`Klart ${currentYear}`}
                            icon={<CheckCircle size={20} />}
                            count={filteredCompleted.length}
                            loading={completedLoading}
                            emptyMessage={`Inga slutförda ärenden för ${currentYear}`}
                            onShowAll={filteredCompleted.length > 20 ? handleShowAllCompleted : undefined}
                            subtitle=""
                        >
                            {groupedByBuilding
                                ? renderTaskGroups('completed', completedWorkOrderTasks, renderCompletedTask, 'Klart', <CheckCircle size={GROUP_ICON_SIZE} aria-hidden="true" />)
                                : completedWorkOrderTasks.slice(0, 20).map(renderCompletedTask)}
                        </ColumnCard>
                    </div>
                    </div>
                </div>
            </div>
            )}

            {/* Issue Detail Modal */}
            {selectedIssue && (
                <IssueDetailModal
                    show={true}
                    onHide={() => closeDetailModal('issue')}
                    issue={selectedIssue}
                    onConvert={handleConvertFromDetail}
                    onArchive={handleArchiveIssue}
                    onIssueChanged={refreshIssues}
                    onSnooze={async (snoozeDate) => {
                        await snoozeIssue(selectedIssue.key, snoozeDate);
                        closeDetailModal('issue');
                    }}
                    onUnsnooze={async () => {
                        await unsnoozeIssue(selectedIssue.key);
                        closeDetailModal('issue');
                    }}
                />
            )}

            {/* Convert Issue to Work Order Modal */}
            {convertingIssue && (
                <CreateWorkOrderModal
                    show={true}
                    onHide={() => setConvertingIssue(null)}
                    issue={convertingIssue}
                    issueComponentLabel={convertingIssueLabel}
                    onSuccess={handleIssueConverted}
                    onExternalUserCreated={refreshUsers}
                />
            )}

            {/* Maintenance Detail Modal */}
            {viewingMaintenance && (
                <MaintenanceDetailModal
                    show={true}
                    onHide={() => closeDetailModal('maintenance')}
                    item={viewingMaintenance}
                    onConvert={handleMaintenanceConvert}
                    onSnooze={async (snoozeDate) => {
                        await snoozeMaintenance(
                            viewingMaintenance.buildingKey,
                            viewingMaintenance.componentKey,
                            viewingMaintenance.templateId,
                            snoozeDate
                        );
                    }}
                    onUnsnooze={async () => {
                        await unsnoozeMaintenance(
                            viewingMaintenance.buildingKey,
                            viewingMaintenance.componentKey,
                            viewingMaintenance.templateId
                        );
                    }}
                />
            )}

            {/* Convert Maintenance to Work Order Modal */}
            {convertingMaintenance && (
                <CreateWorkOrderModal
                    show={true}
                    onHide={() => {
                        setConvertingMaintenance(null);
                        closeDetailModal('maintenance');
                    }}
                    maintenanceItem={convertingMaintenance}
                    onSuccess={handleMaintenanceConverted}
                    onExternalUserCreated={refreshUsers}
                />
            )}

            {/* Standalone Create Work Order Modal */}
            {showCreateWorkOrder && (
                <CreateWorkOrderModal
                    show={true}
                    onHide={() => setShowCreateWorkOrder(false)}
                    onSuccess={handleWorkOrderCreated}
                    onExternalUserCreated={refreshUsers}
                />
            )}

            {/* Duplicate Work Order Modal (create modal pre-filled from a source) */}
            {duplicateSource && (
                <CreateWorkOrderModal
                    show={true}
                    onHide={() => setDuplicateSource(null)}
                    sourceWorkOrder={duplicateSource}
                    onSuccess={handleDuplicateCreated}
                    onExternalUserCreated={refreshUsers}
                />
            )}

            {/* Tilldela modal (adds assignees/delegates, never replaces) */}
            {assigningWorkOrder && (
                <AssignWorkOrderModal
                    show={true}
                    onHide={() => setAssigningWorkOrderKey(null)}
                    workOrder={assigningWorkOrder}
                    internalUsers={activeUsers}
                    externalUsers={externalUserList}
                    onAssigned={refreshWorkOrders}
                    onExternalUserCreated={refreshUsers}
                />
            )}

            {/* Due Date Modal */}
            {dueDateTask && (
                <DueDateModal
                    show={true}
                    onHide={handleDueDateClose}
                    task={dueDateTask}
                    onConfirm={handleDueDateConfirm}
                    initialDate={dueDateProposal}
                />
            )}

            {/* Task Detail Modal (for inspections only) */}
            {viewingTask && (
                <TaskDetailModal
                    show={true}
                    onHide={handleTaskDetailClose}
                    task={viewingTask}
                    onAssign={handleTaskDetailAssign}
                    onNavigate={handleTaskDetailNavigate}
                    getUserName={getUserName}
                    onSnooze={viewingTask.inspection?.buildingId ? async (snoozeDate) => {
                        await snoozeBuildingCheck(
                            viewingTask.inspection!.buildingId!,
                            viewingTask.inspection!.checkKey,
                            snoozeDate
                        );
                        closeDetailModal('inspection');
                    } : undefined}
                    onUnsnooze={viewingTask.inspection?.buildingId ? async () => {
                        await unsnoozeBuildingCheck(
                            viewingTask.inspection!.buildingId!,
                            viewingTask.inspection!.checkKey
                        );
                        closeDetailModal('inspection');
                    } : undefined}
                />
            )}

            {/* Work Order Detail Modal (for all work order columns) */}
            {viewingWorkOrderKey && (
                <WorkOrderDetailModal
                    show={true}
                    onHide={() => closeDetailModal('wo')}
                    workOrderKey={viewingWorkOrderKey}
                    onCompleted={() => {
                        closeDetailModal('wo');
                        refreshWorkOrders();
                        refreshCompleted();
                    }}
                    onCancelled={() => {
                        closeDetailModal('wo');
                        refreshWorkOrders();
                        refreshCompleted();
                    }}
                    onDeleted={() => {
                        closeDetailModal('wo');
                        refreshWorkOrders();
                        refreshCompleted();
                        // The source issue may have been restored to "Att hantera".
                        refreshIssues();
                    }}
                    onReissued={() => {
                        closeDetailModal('wo');
                        refreshWorkOrders();
                        refreshCompleted();
                    }}
                    onChanged={() => {
                        // In-modal edit (assignee/project link) changed board-visible data;
                        // refresh the list without closing the modal
                        refreshWorkOrders();
                        refreshCompleted();
                    }}
                    onDuplicate={handleDuplicateWorkOrder}
                    getUserName={getUserName}
                    onExternalUserCreated={refreshUsers}
                />
            )}

            {/* Archived Work Orders Modal */}
            <ArchivedWorkOrdersModal
                show={showArchivedWorkOrders}
                onHide={() => setShowArchivedWorkOrders(false)}
                onReissued={() => {
                    refreshWorkOrders();
                    refreshCompleted();
                }}
                onDeleted={refreshIssues}
            />

            {/* Archived Issues Modal */}
            <ArchivedIssuesModal
                show={showArchivedIssues}
                onHide={() => setShowArchivedIssues(false)}
            />
        </div>
    );
};

export default ManagementPage;
